import clsx from 'clsx';
import { SettingItemActions } from '../../SettingItemActions';
import React, { ChangeEvent, useState } from 'react';
import { useForm } from '../../../../../hooks/useForm';
import { Select, SelectItem } from '../../../../../components/Select';
import { Input } from '../../../../../components/Input';
import { Checkbox } from '../../../../../components/Checkbox';
import { Label } from '../../../../../components/Label';
import { SettingTag } from '../../SettingTag';

interface Props {
  index: number;
  deviceModel: CreateDeviceModel;
  onRemove?(index: number): void;
  onSave?(item: CreateDeviceModel, index: number): void;
}

interface Content extends Props {
  editing: boolean;
}

interface Form extends Content {
  values: CreateDeviceModel;
  onChange(event: ChangeEvent<HTMLInputElement>): void;
  updateField(field: keyof CreateDeviceModel, value: any): void;
}

export const DeviceModel = (props: Props) => {
  const [editing, setEditing] = useState(false);
  const { deviceModel, index, onRemove = null, onSave = null } = props;
  const { values, onChange, updateField } = useForm<CreateDeviceModel>(
    deviceModel,
    () => null
  );

  return (
    <div
      className={clsx(
        'w-full inline-flex items-center justify-between gap-4 h-14'
      )}
    >
      <EditDeviceModel
        editing={editing}
        onChange={onChange}
        updateField={updateField}
        values={values}
        {...props}
      />
      <DeviceModelContent editing={editing} {...props} />
      {onRemove && onSave ? (
        <SettingItemActions
          canSave={
            values.name.trim() !== '' && values.clientVersion.trim() !== ''
          }
          editing={editing}
          onEdit={() => setEditing(true)}
          onRemove={() => onRemove(index)}
          onSave={() => {
            onSave(values, index);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
};

const EditDeviceModel = ({ editing, values, onChange, updateField }: Form) => {
  const defaultProps: any = {
    variant: 'contained',
    onChange: onChange,
    required: true,
  };

  if (editing === false) return null;
  return (
    <fieldset className="inline-flex items-center gap-4">
      <Select
        label="System"
        value={values.system ?? ''}
        onChange={(event) => updateField('system', event.target.value)}
        required
        variant="contained"
      >
        <SelectItem value="MR">MediaRoom</SelectItem>
        <SelectItem value="MF">MediaFirst</SelectItem>
      </Select>
      <Input
        name="name"
        label="Model Name"
        value={values.name}
        {...defaultProps}
      />
      <Input
        name="clientVersion"
        label="Client Version"
        value={values.clientVersion}
        {...defaultProps}
      />
      <Checkbox
        label="DVR?"
        checked={values.canDVR}
        onChange={(event) => updateField('canDVR', event.target.checked)}
      />
    </fieldset>
  );
};

const DeviceModelContent = ({ editing, deviceModel }: Content) => {
  if (editing) return null;

  const formatSystemType = (type?: string) => {
    switch (type) {
      case 'MR':
        return 'Mediaroom';
      case 'MF':
        return 'MediaFirst';
      default:
        return 'N/A';
    }
  };

  const renderDvrTag = () => {
    if (deviceModel.canDVR === false)
      return <div className="opacity-0 !min-w-[60px] max-w-[70px]" />;
    return <SettingTag name="DVR" color="amber" />;
  };

  return (
    <div className="inline-flex items-center justify-between gap-4 w-full">
      <SettingTag
        name={formatSystemType(deviceModel.system || '')}
        color={deviceModel.system === 'MR' ? 'violet' : 'teal'}
      />
      <div className="inline-flex gap-8 items-center w-80">
        <Label text="Model Name:" align="left">
          {deviceModel.name}
        </Label>
        <Label text="Client Version:" align="left">
          {deviceModel.clientVersion}
        </Label>
      </div>
      {renderDvrTag()}
    </div>
  );
};
